// ResultsPanel.tsx — everything below the scenario editor for a single computed
// scenario: flag banners, real-terms toggle, summary cards, time-series chart, cost
// waterfall, events ledger, and (forecast mode only) the Monte-Carlo / Renditeband
// section.
import type { ForecastConfig, SimulationResult } from '@etf-cockpit/engine';
import { FlagBanners } from './FlagBanners.js';
import { SummaryCards } from './SummaryCards.js';
import { SimulationChart } from './SimulationChart.js';
import { WaterfallChart } from './WaterfallChart.js';
import { EventsLedger } from './EventsLedger.js';
import { MonteCarloSection } from './MonteCarloSection.js';
import { RealTermsToggle } from './RealTermsToggle.js';
import { BacktestReturnStats } from './BacktestReturnStats.js';
import { percentToBp } from '../lib/convert.js';
import { deflateResult } from '../lib/realTerms.js';
import type { RealTermsSettings } from '../lib/realTerms.js';
import type { BacktestMeta } from '../lib/types.js';
import type { WaterfallMode } from '../lib/waterfall-geometry.js';
import type { MonteCarloSettings } from '../lib/montecarlo.js';

export interface ResultsPanelProps {
  result: SimulationResult;
  mode: WaterfallMode;
  meta?: BacktestMeta | undefined;
  /** The ForecastConfig `result` was computed from — present only for mode==='forecast'.
   * Monte-Carlo / Renditeband is a forecast-only feature (backtest has no synthetic
   * return to perturb), so the section only renders when this AND the settings/handler
   * below are all present. */
  forecastConfig?: ForecastConfig | undefined;
  monteCarloSettings?: MonteCarloSettings | undefined;
  onMonteCarloSettingsChange?: ((settings: MonteCarloSettings) => void) | undefined;
  realTerms: RealTermsSettings;
  onRealTermsChange: (settings: RealTermsSettings) => void;
}

/** Maps ForecastConfig.extraContributions[].monthIndex back to the actual calendar date,
 * for the Verlauf chart's markers. Cheap and exact: the accumulation loop pushes exactly
 * one series point per elapsed month, in order (forecast.ts), so series[monthIndex].date
 * IS that month's date — no separate date-math dependency needed here. */
function extraContributionDates(
  result: SimulationResult,
  forecastConfig: ForecastConfig | undefined,
): string[] {
  if (!forecastConfig?.extraContributions) return [];
  const dates: string[] = [];
  for (const extra of forecastConfig.extraContributions) {
    const point = result.series[extra.monthIndex];
    if (point) dates.push(point.date);
  }
  return dates;
}

export function ResultsPanel({
  result,
  mode,
  meta,
  forecastConfig,
  monteCarloSettings,
  onMonteCarloSettingsChange,
  realTerms,
  onRealTermsChange,
}: ResultsPanelProps) {
  const inflationBp = realTerms.enabled ? percentToBp(realTerms.inflationPercent) : 0;
  // deflateResult(result, 0) short-circuits to the SAME object — cheap and correct even
  // when the toggle is off, so this is always safe to compute unconditionally.
  const displayResult = deflateResult(result, inflationBp);

  return (
    <div>
      <FlagBanners flags={result.flags} rangeClamped={meta?.rangeClamped} />

      <div class="card">
        <RealTermsToggle settings={realTerms} onChange={onRealTermsChange} />
      </div>

      <div class="card">
        <h2>
          Ergebnis
          {realTerms.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
        </h2>
        <SummaryCards result={displayResult} />
      </div>

      <div class="card">
        <h2>
          Verlauf
          {realTerms.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
        </h2>
        <SimulationChart
          series={displayResult.series}
          crossoverDate={result.crossover.cumulativeDate}
          extraContributionDates={extraContributionDates(result, forecastConfig)}
        />
      </div>

      {mode === 'backtest' && meta ? <BacktestReturnStats meta={meta} result={result} /> : null}

      {mode === 'forecast' && forecastConfig && monteCarloSettings && onMonteCarloSettingsChange ? (
        <MonteCarloSection
          forecastConfig={forecastConfig}
          settings={monteCarloSettings}
          onSettingsChange={onMonteCarloSettingsChange}
          realTerms={realTerms}
        />
      ) : null}

      <div class="card">
        <h2>
          Kostenwasserfall
          {realTerms.enabled ? <span class="badge-real-terms">in heutiger Kaufkraft</span> : null}
        </h2>
        <WaterfallChart waterfall={displayResult.waterfall} mode={mode} />
      </div>

      <div class="card">
        <EventsLedger events={result.events} />
      </div>
    </div>
  );
}
